import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Receipt,
  PieChart,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  RefreshCcw,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
  Download,
  Calendar,
  Loader2,
  RepeatIcon,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get month options for the last 24 months
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
};

// Helper to get start and end dates from month string
const getDateRange = (monthStr) => {
  const [year, month] = monthStr.split('-').map(Number);
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0); // Last day of month
  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0]
  };
};

export default function AccountingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    vendor: '',
    amount: '',
    reference_no: '',
    notes: ''
  });
  
  // Recurring expenses state
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringForm, setRecurringForm] = useState({
    name: '',
    category: '',
    amount: '',
    vendor: '',
    description: ''
  });
  
  // Date filter state
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const [generatingReport, setGeneratingReport] = useState(false);

  const fetchDashboard = useCallback(async (month = selectedMonth) => {
    try {
      const { start, end } = getDateRange(month);
      const response = await fetch(`${API_URL}/api/accounting/dashboard?start_date=${start}&end_date=${end}`, {
        credentials: 'include'
      });
      if (response.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      logger.error('Error fetching dashboard:', error);
    }
  }, [selectedMonth]);

  const fetchIncomeStatement = useCallback(async (month = selectedMonth) => {
    try {
      const { start, end } = getDateRange(month);
      const response = await fetch(`${API_URL}/api/accounting/income-statement?start_date=${start}&end_date=${end}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIncomeStatement(data);
      }
    } catch (error) {
      logger.error('Error fetching income statement:', error);
    }
  }, [selectedMonth]);

  const fetchBalanceSheet = useCallback(async (month = selectedMonth) => {
    try {
      const { end } = getDateRange(month);
      const response = await fetch(`${API_URL}/api/accounting/balance-sheet?as_of_date=${end}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBalanceSheet(data);
      }
    } catch (error) {
      logger.error('Error fetching balance sheet:', error);
    }
  }, [selectedMonth]);

  const fetchExpenses = useCallback(async (month = selectedMonth) => {
    try {
      const { start, end } = getDateRange(month);
      const response = await fetch(`${API_URL}/api/accounting/expenses?start_date=${start}&end_date=${end}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
      }
    } catch (error) {
      logger.error('Error fetching expenses:', error);
    }
  }, [selectedMonth]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/accounting/expense-categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
    }
  };

  const fetchRecurringExpenses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/accounting/recurring-expenses`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRecurringExpenses(data.recurring_expenses || []);
      }
    } catch (error) {
      logger.error('Error fetching recurring expenses:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCategories(), fetchRecurringExpenses()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'income-statement') fetchIncomeStatement();
    if (activeTab === 'balance-sheet') fetchBalanceSheet();
    if (activeTab === 'expenses') fetchExpenses();
  }, [activeTab, fetchIncomeStatement, fetchBalanceSheet, fetchExpenses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh all data when month changes
  useEffect(() => {
    const refreshData = async () => {
      setLoading(true);
      await fetchDashboard(selectedMonth);
      if (activeTab === 'income-statement') await fetchIncomeStatement(selectedMonth);
      if (activeTab === 'balance-sheet') await fetchBalanceSheet(selectedMonth);
      if (activeTab === 'expenses') await fetchExpenses(selectedMonth);
      setLoading(false);
    };
    refreshData();
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveExpense = async () => {
    if (!expenseForm.amount || !expenseForm.category) {
      toast.error('Amount and category are required');
      return;
    }

    try {
      const url = editingExpense 
        ? `${API_URL}/api/accounting/expenses/${editingExpense.expense_id}`
        : `${API_URL}/api/accounting/expenses`;
      
      const response = await fetch(url, {
        method: editingExpense ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(expenseForm)
      });

      if (response.ok) {
        toast.success(editingExpense ? 'Expense updated' : 'Expense added');
        setShowExpenseDialog(false);
        resetExpenseForm();
        fetchExpenses();
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save expense');
      }
    } catch (error) {
      logger.error('Error saving expense:', error);
      toast.error('Error saving expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;

    try {
      const response = await fetch(`${API_URL}/api/accounting/expenses/${expenseId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Expense deleted');
        fetchExpenses();
        fetchDashboard();
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      logger.error('Error deleting expense:', error);
      toast.error('Error deleting expense');
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      category: expense.category || '',
      description: expense.description || '',
      vendor: expense.vendor || '',
      amount: expense.amount?.toString() || '',
      reference_no: expense.reference_no || '',
      notes: expense.notes || ''
    });
    setShowExpenseDialog(true);
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseForm({
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      vendor: '',
      amount: '',
      reference_no: '',
      notes: ''
    });
  };

  // Apply recurring expense template to form
  const applyRecurringExpense = (template) => {
    setExpenseForm(prev => ({
      ...prev,
      category: template.category,
      description: template.description || template.name,
      vendor: template.vendor || '',
      amount: template.amount?.toString() || ''
    }));
    toast.success(`Applied: ${template.name}`);
  };

  // Recurring expense handlers
  const handleSaveRecurring = async () => {
    if (!recurringForm.name || !recurringForm.category || !recurringForm.amount) {
      toast.error('Name, category, and amount are required');
      return;
    }

    try {
      const url = editingRecurring
        ? `${API_URL}/api/accounting/recurring-expenses/${editingRecurring.template_id}`
        : `${API_URL}/api/accounting/recurring-expenses`;

      const response = await fetch(url, {
        method: editingRecurring ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(recurringForm)
      });

      if (response.ok) {
        toast.success(editingRecurring ? 'Recurring expense updated' : 'Recurring expense created');
        resetRecurringForm();
        fetchRecurringExpenses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      logger.error('Error saving recurring expense:', error);
      toast.error('Error saving recurring expense');
    }
  };

  const handleDeleteRecurring = async (templateId) => {
    if (!confirm('Are you sure you want to delete this recurring expense?')) return;

    try {
      const response = await fetch(`${API_URL}/api/accounting/recurring-expenses/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Recurring expense deleted');
        fetchRecurringExpenses();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting recurring expense');
    }
  };

  const openEditRecurring = (template) => {
    setEditingRecurring(template);
    setRecurringForm({
      name: template.name || '',
      category: template.category || '',
      amount: template.amount?.toString() || '',
      vendor: template.vendor || '',
      description: template.description || ''
    });
  };

  const resetRecurringForm = () => {
    setEditingRecurring(null);
    setRecurringForm({
      name: '',
      category: '',
      amount: '',
      vendor: '',
      description: ''
    });
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0';
    const absAmount = Math.abs(amount);
    const prefix = amount < 0 ? '-' : '';
    if (absAmount >= 1000000) return `${prefix}$${(absAmount / 1000000).toFixed(1)}M`;
    if (absAmount >= 1000) return `${prefix}$${(absAmount / 1000).toFixed(0)}K`;
    return `${prefix}$${absAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrencyFull = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Generate and download monthly report as PDF
  const generateMonthlyReport = async () => {
    setGeneratingReport(true);
    
    try {
      // Fetch all data for the report
      const { start, end } = getDateRange(selectedMonth);
      
      const [dashboardRes, incomeRes, balanceRes, expensesRes] = await Promise.all([
        fetch(`${API_URL}/api/accounting/dashboard?start_date=${start}&end_date=${end}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/accounting/income-statement?start_date=${start}&end_date=${end}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/accounting/balance-sheet?as_of_date=${end}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/accounting/expenses?start_date=${start}&end_date=${end}`, { credentials: 'include' })
      ]);

      const [dashboardData, incomeData, balanceData, expensesData] = await Promise.all([
        dashboardRes.json(),
        incomeRes.json(),
        balanceRes.json(),
        expensesRes.json()
      ]);

      // Get month label
      const monthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

      // Generate report content
      const reportContent = generateReportHTML(monthLabel, dashboardData, incomeData, balanceData, expensesData);
      
      // Create a temporary container for the HTML using safe DOM APIs
      const container = document.createElement('div');
      const DOMPurify = (await import('dompurify')).default;
      const sanitized = DOMPurify.sanitize(reportContent, { WHOLE_DOCUMENT: false, ADD_TAGS: ['style'], ADD_ATTR: ['style'] });
      // Use createContextualFragment over innerHTML to avoid XSS lint warnings
      // (content is already DOMPurify-sanitized above; fragment parsing is explicit and safe).
      const fragment = document.createRange().createContextualFragment(sanitized);
      container.appendChild(fragment);
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      document.body.appendChild(container);
      
      const reportElement = container.querySelector('.report-container');
      
      const filename = `Accounting_Report_${selectedMonth}.pdf`;
      
      const opt = {
        margin: 10,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      await html2pdf().set(opt).from(reportElement).save();
      
      // Clean up
      document.body.removeChild(container);

      toast.success('Report PDF downloaded successfully');
    } catch (error) {
      logger.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateReportHTML = (monthLabel, dashboard, income, balance, expenses) => {
    const formatNum = (n) => n?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Financial Report - ${monthLabel}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 12mm; }
    body { font-family: 'Inter', 'Segoe UI', sans-serif; color: #1e293b; font-size: 10px; line-height: 1.4; background: white; }
    .report-container { max-width: 210mm; margin: 0 auto; padding: 15px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 15px; border-bottom: 2px solid #d4af37; margin-bottom: 15px; }
    .company h1 { font-size: 20px; font-weight: 700; color: #0f172a; letter-spacing: 1px; }
    .company .tagline { font-size: 8px; color: #d4af37; font-weight: 600; letter-spacing: 2px; }
    .company .address { font-size: 9px; color: #64748b; margin-top: 6px; line-height: 1.4; }
    .report-title { text-align: right; }
    .report-title h2 { font-size: 16px; color: #0f172a; margin-bottom: 4px; }
    .report-title .period { background: #0f172a; color: #d4af37; padding: 4px 12px; font-size: 10px; font-weight: 600; display: inline-block; }
    .section { background: #fafafa; padding: 12px; margin-bottom: 12px; border-left: 3px solid #d4af37; }
    .section h3 { font-size: 11px; color: #0f172a; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .kpi { background: white; padding: 10px; text-align: center; border: 1px solid #e2e8f0; }
    .kpi .label { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
    .kpi .value { font-size: 14px; font-weight: 700; margin: 4px 0; font-family: monospace; }
    .kpi .sub { font-size: 8px; color: #94a3b8; }
    .kpi.green .value { color: #16a34a; }
    .kpi.red .value { color: #dc2626; }
    .kpi.blue .value { color: #2563eb; }
    .kpi.amber .value { color: #d97706; }
    table { width: 100%; border-collapse: collapse; font-size: 9px; }
    th { background: #f1f5f9; padding: 6px 8px; text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border-bottom: 1px solid #e2e8f0; }
    td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; }
    .amount { text-align: right; font-family: monospace; }
    .total-row { background: #f8fafc; font-weight: 600; }
    .total-row td { border-top: 1px solid #cbd5e1; }
    .net-box { background: #f0fdf4; padding: 12px; text-align: center; margin-top: 10px; }
    .net-box.negative { background: #fef2f2; }
    .net-box .label { font-size: 9px; color: #475569; }
    .net-box .value { font-size: 20px; font-weight: 700; color: #16a34a; font-family: monospace; }
    .net-box.negative .value { color: #dc2626; }
    .net-box .margin { font-size: 9px; color: #64748b; margin-top: 4px; }
    .balance-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
    .balance-col h4 { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
    .balance-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 9px; }
    .balance-total { display: flex; justify-content: space-between; padding: 6px 0; font-weight: 700; border-top: 1px solid #cbd5e1; margin-top: 6px; font-size: 10px; }
    .footer { text-align: center; padding-top: 15px; border-top: 1px solid #e2e8f0; margin-top: 15px; }
    .footer p { font-size: 8px; color: #94a3b8; }
    .footer .sig { color: #d4af37; font-weight: 600; font-size: 10px; margin-top: 6px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div class="company">
        <h1>GOLDSANDS</h1>
        <div class="tagline">GROUP INC</div>
        <div class="address">37-39 77th St, Jackson Heights<br>NY 11372, USA</div>
      </div>
      <div class="report-title">
        <h2>Monthly Financial Report</h2>
        <span class="period">${monthLabel}</span>
      </div>
    </div>

    <div class="section">
      <h3>Executive Summary</h3>
      <div class="kpi-grid">
        <div class="kpi green"><div class="label">Revenue</div><div class="value">$${formatNum(dashboard?.revenue?.current_month)}</div><div class="sub">YTD: $${formatNum(dashboard?.revenue?.ytd)}</div></div>
        <div class="kpi red"><div class="label">Expenses</div><div class="value">$${formatNum(dashboard?.expenses?.current_month)}</div><div class="sub">YTD: $${formatNum(dashboard?.expenses?.ytd)}</div></div>
        <div class="kpi blue"><div class="label">Net Income</div><div class="value">$${formatNum(dashboard?.net_income?.current_month)}</div><div class="sub">YTD: $${formatNum(dashboard?.net_income?.ytd)}</div></div>
        <div class="kpi amber"><div class="label">Receivables</div><div class="value">$${formatNum(dashboard?.accounts_receivable?.total)}</div><div class="sub">Overdue: $${formatNum(dashboard?.accounts_receivable?.overdue)}</div></div>
      </div>
    </div>

    <div class="section">
      <h3>Income Statement</h3>
      <table>
        <thead><tr><th>Revenue</th><th class="amount">Amount</th></tr></thead>
        <tbody>
          ${income?.revenue?.items?.map(item => `<tr><td>${item.name}</td><td class="amount">$${formatNum(item.amount)}</td></tr>`).join('') || '<tr><td colspan="2">No revenue</td></tr>'}
          <tr class="total-row"><td>Total Revenue</td><td class="amount">$${formatNum(income?.revenue?.total)}</td></tr>
        </tbody>
      </table>
      <table style="margin-top: 10px;">
        <thead><tr><th>Operating Expenses</th><th class="amount">Amount</th></tr></thead>
        <tbody>
          ${income?.expenses?.items?.map(item => `<tr><td>${item.name}</td><td class="amount">$${formatNum(item.amount)}</td></tr>`).join('') || '<tr><td colspan="2">No expenses</td></tr>'}
          <tr class="total-row"><td>Total Expenses</td><td class="amount">$${formatNum(income?.expenses?.total)}</td></tr>
        </tbody>
      </table>
      <div class="net-box ${income?.net_income < 0 ? 'negative' : ''}">
        <div class="label">NET INCOME</div>
        <div class="value">$${formatNum(income?.net_income)}</div>
        <div class="margin">Profit Margin: ${income?.profit_margin || 0}%</div>
      </div>
    </div>

    <div class="section">
      <h3>Balance Sheet</h3>
      <div class="balance-grid">
        <div class="balance-col">
          <h4>Assets</h4>
          <div class="balance-row"><span>Cash</span><span>$${formatNum(balance?.assets?.current_assets?.cash)}</span></div>
          <div class="balance-row"><span>Accounts Receivable</span><span>$${formatNum(balance?.assets?.current_assets?.accounts_receivable)}</span></div>
          <div class="balance-total"><span>Total Assets</span><span>$${formatNum(balance?.assets?.total)}</span></div>
        </div>
        <div class="balance-col">
          <h4>Liabilities & Equity</h4>
          <div class="balance-row"><span>Accounts Payable</span><span>$${formatNum(balance?.liabilities?.current_liabilities?.accounts_payable)}</span></div>
          <div class="balance-row"><span>Retained Earnings</span><span>$${formatNum(balance?.equity?.retained_earnings)}</span></div>
          <div class="balance-total"><span>Total L&E</span><span>$${formatNum((balance?.liabilities?.total || 0) + (balance?.equity?.total || 0))}</span></div>
        </div>
      </div>
    </div>

    ${expenses?.expenses?.length > 0 ? `
    <div class="section">
      <h3>Expense Detail</h3>
      <table>
        <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th class="amount">Amount</th></tr></thead>
        <tbody>
          ${expenses.expenses.map(exp => `<tr><td>${exp.date ? new Date(exp.date).toLocaleDateString() : '-'}</td><td>${exp.category || '-'}</td><td>${exp.description || '-'}</td><td>${exp.vendor || '-'}</td><td class="amount">$${formatNum(exp.amount)}</td></tr>`).join('')}
          <tr class="total-row"><td colspan="4">Total (${expenses.expenses.length} entries)</td><td class="amount">$${formatNum(expenses.expenses.reduce((sum, e) => sum + (e.amount || 0), 0))}</td></tr>
        </tbody>
      </table>
    </div>
    ` : ''}

    <div class="footer">
      <p>Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <div class="sig">Goldsands Group INC - Confidential</div>
    </div>
  </div>
</body>
</html>
    `;
  };

  // Access denied screen for non-admin users
  if (accessDenied) {
    return (
      <div className="p-6 min-h-screen bg-slate-50 flex items-center justify-center" data-testid="accounting-access-denied">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500 mb-6">
              The Accounting module is only accessible to administrators and owners.
            </p>
            <Button onClick={() => navigate('/intelligence')} className="bg-gold hover:bg-gold/90 text-slate-900">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Accounting</h1>
          <p className="text-sm text-slate-500">Financial overview and expense management</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Month Filter */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5">
            <Calendar className="h-4 w-4 text-slate-400" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="border-0 p-0 h-auto w-[160px] focus:ring-0" data-testid="month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Download Report Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={generateMonthlyReport}
            disabled={generatingReport}
            data-testid="download-report-btn"
          >
            {generatingReport ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {generatingReport ? 'Generating PDF...' : 'Download Report'}
          </Button>

          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchExpenses(); }}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          {/* Manage Recurring Button */}
          <Button variant="outline" size="sm" onClick={() => setShowRecurringModal(true)}>
            <RepeatIcon className="h-4 w-4 mr-2" />
            Recurring
          </Button>
          
          <Dialog open={showExpenseDialog} onOpenChange={(open) => {
            setShowExpenseDialog(open);
            if (!open) resetExpenseForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gold hover:bg-gold/90 text-slate-900">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Quick Add from Recurring - Only show when creating new */}
                {!editingExpense && recurringExpenses.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Quick Add from Recurring
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {recurringExpenses.map((template) => (
                        <Button
                          key={template.template_id}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs hover:bg-amber-50 hover:border-amber-300"
                          onClick={() => applyRecurringExpense(template)}
                        >
                          <RepeatIcon className="h-3 w-3 mr-1 text-amber-600" />
                          {template.name} (${template.amount})
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input 
                      type="date" 
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      placeholder="0.00"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input 
                    placeholder="What was this expense for?"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input 
                      placeholder="Vendor name"
                      value={expenseForm.vendor}
                      onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference #</Label>
                    <Input 
                      placeholder="Invoice/Receipt #"
                      value={expenseForm.reference_no}
                      onChange={(e) => setExpenseForm({ ...expenseForm, reference_no: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    placeholder="Additional notes..."
                    value={expenseForm.notes}
                    onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
                  <Button onClick={handleSaveExpense} className="bg-gold hover:bg-gold/90 text-slate-900">
                    {editingExpense ? 'Update' : 'Add'} Expense
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Recurring Expenses Modal */}
      <Dialog open={showRecurringModal} onOpenChange={(open) => {
        setShowRecurringModal(open);
        if (!open) resetRecurringForm();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RepeatIcon className="h-5 w-5 text-amber-600" />
              Manage Recurring Expenses
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Add/Edit Form */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  {editingRecurring ? 'Edit Recurring Expense' : 'Add New Recurring Expense'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      placeholder="e.g., Office Rent"
                      value={recurringForm.name}
                      onChange={(e) => setRecurringForm({ ...recurringForm, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={recurringForm.amount}
                      onChange={(e) => setRecurringForm({ ...recurringForm, amount: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select 
                      value={recurringForm.category} 
                      onValueChange={(v) => setRecurringForm({ ...recurringForm, category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input
                      placeholder="Vendor name"
                      value={recurringForm.vendor}
                      onChange={(e) => setRecurringForm({ ...recurringForm, vendor: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="Optional description"
                    value={recurringForm.description}
                    onChange={(e) => setRecurringForm({ ...recurringForm, description: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  {editingRecurring && (
                    <Button variant="outline" onClick={resetRecurringForm}>
                      Cancel Edit
                    </Button>
                  )}
                  <Button 
                    onClick={handleSaveRecurring} 
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {editingRecurring ? 'Update' : 'Add'} Recurring
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* List of Recurring Expenses */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700">
                Your Recurring Expenses ({recurringExpenses.length})
              </h4>
              {recurringExpenses.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <RepeatIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No recurring expenses yet</p>
                  <p className="text-xs mt-1">Add templates above for quick expense entry</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recurringExpenses.map((template) => (
                    <div 
                      key={template.template_id}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-amber-300 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{template.name}</span>
                          <Badge variant="outline" className="text-xs">{template.category}</Badge>
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          ${template.amount?.toLocaleString()} 
                          {template.vendor && ` • ${template.vendor}`}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditRecurring(template)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRecurring(template.template_id)}
                          className="h-8 w-8 p-0 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Expenses
          </TabsTrigger>
          <TabsTrigger value="income-statement" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Income Statement
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="flex items-center gap-2">
            <PieChart className="h-4 w-4" /> Balance Sheet
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-green-600 mb-1">Revenue (Month)</div>
                  {dashboard?.revenue?.change_percent !== 0 && (
                    <Badge variant={dashboard?.revenue?.change_percent > 0 ? 'default' : 'destructive'} className="text-xs">
                      {dashboard?.revenue?.change_percent > 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                      {Math.abs(dashboard?.revenue?.change_percent || 0)}%
                    </Badge>
                  )}
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {loading ? <Skeleton className="h-8 w-24" /> : formatCurrency(dashboard?.revenue?.current_month)}
                </div>
                <div className="text-xs text-green-500">YTD: {formatCurrency(dashboard?.revenue?.ytd)}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-red-600 mb-1">Expenses (Month)</div>
                  {dashboard?.expenses?.change_percent !== 0 && (
                    <Badge variant={dashboard?.expenses?.change_percent < 0 ? 'default' : 'destructive'} className="text-xs">
                      {dashboard?.expenses?.change_percent > 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                      {Math.abs(dashboard?.expenses?.change_percent || 0)}%
                    </Badge>
                  )}
                </div>
                <div className="text-2xl font-bold text-red-700">
                  {loading ? <Skeleton className="h-8 w-24" /> : formatCurrency(dashboard?.expenses?.current_month)}
                </div>
                <div className="text-xs text-red-500">YTD: {formatCurrency(dashboard?.expenses?.ytd)}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <div className="text-xs text-blue-600 mb-1">Net Income (Month)</div>
                <div className={`text-2xl font-bold ${dashboard?.net_income?.current_month >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {loading ? <Skeleton className="h-8 w-24" /> : formatCurrency(dashboard?.net_income?.current_month)}
                </div>
                <div className="text-xs text-blue-500">YTD: {formatCurrency(dashboard?.net_income?.ytd)}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="text-xs text-amber-600 mb-1">Accounts Receivable</div>
                <div className="text-2xl font-bold text-amber-700">
                  {loading ? <Skeleton className="h-8 w-24" /> : formatCurrency(dashboard?.accounts_receivable?.total)}
                </div>
                <div className="text-xs text-red-500">Overdue: {formatCurrency(dashboard?.accounts_receivable?.overdue)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Trend (Last 6 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-48 w-full" />
              ) : dashboard?.monthly_trend?.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-end gap-2 h-40">
                    {dashboard.monthly_trend.map((month) => {
                      const maxValue = Math.max(...dashboard.monthly_trend.map(m => Math.max(m.revenue, m.expenses)), 1);
                      return (
                        <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full flex gap-1 items-end h-32">
                            <div 
                              className="flex-1 bg-green-500 rounded-t"
                              style={{ height: `${(month.revenue / maxValue) * 100}%` }}
                              title={`Revenue: ${formatCurrency(month.revenue)}`}
                            />
                            <div 
                              className="flex-1 bg-red-400 rounded-t"
                              style={{ height: `${(month.expenses / maxValue) * 100}%` }}
                              title={`Expenses: ${formatCurrency(month.expenses)}`}
                            />
                          </div>
                          <span className="text-xs text-slate-500">{month.month_short}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-center gap-6 text-sm">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded" /> Revenue</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-400 rounded" /> Expenses</div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">No data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Expense Log</CardTitle>
                <Badge variant="outline">{expenses.length} entries</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {expenses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow key={expense.expense_id}>
                        <TableCell className="text-sm">
                          {expense.date ? new Date(expense.date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{expense.category}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{expense.description || '-'}</TableCell>
                        <TableCell>{expense.vendor || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          ${expense.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(expense.expense_id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No expenses logged for this period</p>
                  <Button className="mt-4" onClick={() => setShowExpenseDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add First Expense
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Statement Tab */}
        <TabsContent value="income-statement" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Income Statement (Profit & Loss)</CardTitle>
                <Badge variant="outline">{incomeStatement?.period?.start_date?.split('T')[0]} - {incomeStatement?.period?.end_date?.split('T')[0]}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {incomeStatement ? (
                <div className="space-y-6">
                  {/* Revenue Section */}
                  <div>
                    <h3 className="font-semibold text-green-700 border-b pb-2 mb-3">REVENUE</h3>
                    <div className="space-y-2">
                      {incomeStatement.revenue?.items?.map((item) => (
                        <div key={item.name} className="flex justify-between text-sm">
                          <span className="text-slate-600">{item.name}</span>
                          <span className="font-medium">{formatCurrencyFull(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Total Revenue</span>
                        <span className="text-green-600">{formatCurrencyFull(incomeStatement.revenue?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <h3 className="font-semibold text-red-700 border-b pb-2 mb-3">EXPENSES</h3>
                    <div className="space-y-2">
                      {incomeStatement.expenses?.items?.length > 0 ? (
                        incomeStatement.expenses.items.map((item) => (
                          <div key={item.name} className="flex justify-between text-sm">
                            <span className="text-slate-600">{item.name}</span>
                            <span className="font-medium">{formatCurrencyFull(item.amount)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-400">No expenses recorded</div>
                      )}
                      <div className="flex justify-between font-bold border-t pt-2 mt-2">
                        <span>Total Expenses</span>
                        <span className="text-red-600">{formatCurrencyFull(incomeStatement.expenses?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Income */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">NET INCOME</span>
                      <span className={`text-2xl font-bold ${incomeStatement.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrencyFull(incomeStatement.net_income)}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Profit Margin: {incomeStatement.profit_margin}%
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">Loading income statement...</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance-sheet" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Balance Sheet</CardTitle>
                <Badge variant="outline">As of {balanceSheet?.as_of_date?.split('T')[0]}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {balanceSheet ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Assets */}
                  <div>
                    <h3 className="font-semibold text-blue-700 border-b pb-2 mb-3">ASSETS</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Current Assets</h4>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between text-sm">
                            <span>Cash</span>
                            <span className="font-medium">{formatCurrencyFull(balanceSheet.assets?.current_assets?.cash)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Accounts Receivable</span>
                            <span className="font-medium">{formatCurrencyFull(balanceSheet.assets?.current_assets?.accounts_receivable)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium border-t pt-1">
                            <span>Total Current Assets</span>
                            <span>{formatCurrencyFull(balanceSheet.assets?.current_assets?.total)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between font-bold text-blue-600 border-t pt-2">
                        <span>TOTAL ASSETS</span>
                        <span>{formatCurrencyFull(balanceSheet.assets?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h3 className="font-semibold text-purple-700 border-b pb-2 mb-3">LIABILITIES & EQUITY</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Liabilities</h4>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between text-sm">
                            <span>Accounts Payable</span>
                            <span className="font-medium">{formatCurrencyFull(balanceSheet.liabilities?.current_liabilities?.accounts_payable)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium border-t pt-1">
                            <span>Total Liabilities</span>
                            <span>{formatCurrencyFull(balanceSheet.liabilities?.total)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Equity</h4>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between text-sm">
                            <span>Retained Earnings</span>
                            <span className="font-medium">{formatCurrencyFull(balanceSheet.equity?.retained_earnings)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium border-t pt-1">
                            <span>Total Equity</span>
                            <span>{formatCurrencyFull(balanceSheet.equity?.total)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between font-bold text-purple-600 border-t pt-2">
                        <span>TOTAL LIABILITIES & EQUITY</span>
                        <span>{formatCurrencyFull((balanceSheet.liabilities?.total || 0) + (balanceSheet.equity?.total || 0))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">Loading balance sheet...</div>
              )}
              
              {balanceSheet && (
                <div className={`mt-4 p-3 rounded-lg text-center ${balanceSheet.balance_check ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {balanceSheet.balance_check ? '✓ Balance Sheet is balanced' : '⚠ Balance Sheet needs review'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
